import { useEffect, useState } from "react";
import { photoCopy } from "../iris/maker-language";
import type { CastOption } from "./CastPicker";
import "./photo-offer.css";

const TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_BYTES = 15 * 1024 * 1024;

export type PhotoChoice =
  | { source: "upload"; file: File }
  | { source: "cast"; castAssetId: string; name: string }
  | { source: "generated"; description: string };

type Source = PhotoChoice["source"];

/**
 * Sing with your photo, in the chat: a chip, then where the singer comes from — my own photo
 * (with the consent), someone from my cast, or a newly designed singer. Nothing leaves the
 * browser here; the choice travels with the production when it is made.
 */
export function PhotoOffer({
  onReady,
  castOptions = [],
  open: startOpen = false,
  disabled = false,
}: {
  onReady: (choice: PhotoChoice) => void;
  castOptions?: CastOption[];
  open?: boolean;
  disabled?: boolean;
}) {
  const copy = photoCopy();
  const [open, setOpen] = useState(startOpen);
  const [source, setSource] = useState<Source | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [consent, setConsent] = useState(false);
  const [castId, setCastId] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL?.(preview);
  }, [preview]);

  if (!open)
    return (
      <button type="button" className="quick-chip photo-offer-chip" disabled={disabled} onClick={() => setOpen(true)}>
        {copy.chip}
      </button>
    );

  const choose = (picked: File | undefined) => {
    setError("");
    if (!picked) return;
    if (!TYPES.has(picked.type)) return setError(copy.wrongType);
    if (picked.size > MAX_BYTES) return setError(copy.tooLarge);
    setFile(picked);
    setPreview(URL.createObjectURL?.(picked) || "");
  };
  const castPick = castOptions.find((option) => option.id === castId);
  const ready =
    source === "upload" ? !!file && consent : source === "cast" ? !!castPick : source === "generated" ? description.trim().length >= 3 : false;
  const submit = () => {
    if (source === "upload" && file) onReady({ source, file });
    else if (source === "cast" && castPick) onReady({ source, castAssetId: castPick.id, name: castPick.name });
    else if (source === "generated") onReady({ source, description: description.trim() });
  };
  const sources: [Source, string][] = [
    ["upload", copy.sourceMine],
    ...(castOptions.length ? [["cast", copy.sourceCast] as [Source, string]] : []),
    ["generated", copy.sourceNew],
  ];

  return (
    <section className="photo-offer" aria-label={copy.chip}>
      <div className="photo-offer-sources" role="group">
        {sources.map(([value, label]) => (
          <button key={value} type="button" className={`quick-chip${source === value ? " on" : ""}`} disabled={disabled} onClick={() => setSource(value)}>
            {label}
          </button>
        ))}
      </div>
      {source === "upload" && (
        <>
          <p className="photo-offer-tip">{copy.tip}</p>
          <label className="photo-offer-pick">
            {preview ? <img src={preview} alt="" /> : <span aria-hidden="true">＋</span>}
            <input type="file" accept="image/jpeg,image/png,image/webp" aria-label={copy.label} disabled={disabled} onChange={(event) => choose(event.target.files?.[0])} />
            <small>{file ? copy.change : copy.label}</small>
          </label>
          {error && (
            <p className="photo-offer-error" role="alert">
              {error}
            </p>
          )}
          <label className="photo-offer-consent">
            <input type="checkbox" checked={consent} disabled={disabled} onChange={(event) => setConsent(event.target.checked)} />
            <span>{copy.consent}</span>
          </label>
        </>
      )}
      {source === "cast" && (
        <div className="photo-offer-cast" role="group">
          {castOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-label={option.name}
              aria-pressed={castId === option.id}
              className="photo-offer-person"
              disabled={disabled}
              onClick={() => setCastId(option.id)}
            >
              {option.thumbnail ? <img src={option.thumbnail} alt="" /> : <span aria-hidden="true">{option.name.slice(0, 1)}</span>}
              <small>{option.name}</small>
            </button>
          ))}
        </div>
      )}
      {source === "generated" && (
        <textarea
          className="photo-offer-describe"
          aria-label={copy.describe}
          placeholder={copy.describe}
          maxLength={400}
          rows={3}
          value={description}
          disabled={disabled}
          onChange={(event) => setDescription(event.target.value)}
        />
      )}
      {source && (
        <button type="button" className="quick-chip on" disabled={disabled || !ready} onClick={submit}>
          {copy.continue}
        </button>
      )}
    </section>
  );
}
